import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router';

import {
    ActivityLayout,
    ActivityNavigation,
    ActivityTopBar,
    FloorViewEditor,
    KeyParameterEditor,
    ModuleAddItem,
    ModuleRenderer,
    ModuleReorderGroup,
    ModuleSelectionModal,
    NameModal,
    Scrubber,
    SwingFoundations,
    SwingVideos,
} from '@blocks';
import { useLayoutEditing, useModuleOrdering, useToggle } from '@hooks';
import { activityIdAtom, currentPositionNumberAtom, layoutAtom, studentIdAtom } from '@store';
import { Above, Box, Only } from '@ui';

import { Navigation } from '../../components/Navigation/Navigation';
import { SwingThreadForm } from '../../components/SwingThreadForm/SwingThreadForm';
import { LINKS } from '../../utils/routes';
import * as css from '../screens.css';

export function SwingDetailsScreen() {
    const navigate = useNavigate();

    const [showSwingThreadForm, toggleSwingThreadForm] = useToggle(false);
    const activityId = useAtomValue(activityIdAtom);
    const studentId = useAtomValue(studentIdAtom);
    const currentPosition = useAtomValue(currentPositionNumberAtom);
    const layout = useAtomValue(layoutAtom);

    const {
        isEditing,
        isEditingFloor,
        isEditingKeyParam,
        createLayoutCopy,
        handleCreateLayoutCopy,
        toggleCreateLayoutCopy,
        handleEditingLayout,
        handleEditingFloorLayout,
        handleEditingKeyParam,
        handleNavigateToEditor,
    } = useLayoutEditing();

    const {
        kioskModules,
        kioskModuleIds,
        handleReorder,
        handleRemove,
        handleAdd,
        isLoading,
        setAddItemIndex,
        addItemIndex,
    } = useModuleOrdering();

    return (
        <>
            <ActivityLayout getActivities={LINKS.getActivities}>
                <Navigation isActivity isSwing>
                    <Above bp="md">
                        <ActivityTopBar
                            onCreateNote={toggleSwingThreadForm}
                            isEditingLayout={isEditing}
                            isEditingFloor={isEditingFloor}
                            onEditLayout={handleEditingLayout}
                            onEditFloor={handleEditingFloorLayout}
                        />
                    </Above>
                </Navigation>
                <Box className={css.layout}>
                    <ActivityNavigation
                        onSelectActive={(swingId) => navigate(LINKS.getFullActivity(activityId, swingId))}
                        onDeleteActivity={() => navigate(LINKS.getActivities(studentId))}
                        onEditKeyParameter={handleEditingKeyParam}
                        onNavigateToLiveAnalysis={() => navigate(LINKS.getLiveAnalysis())}
                    />
                    <Box className={css.videoWrapper}>
                        <Only bp="xs" spans={2} avoidRendering>
                            <ActivityTopBar
                                onCreateNote={toggleSwingThreadForm}
                                isEditingLayout={isEditing}
                                isEditingFloor={isEditingFloor}
                                onEditLayout={handleEditingLayout}
                                onEditFloor={handleEditingFloorLayout}
                            />
                        </Only>
                        <Box className={css.video}>
                            <SwingVideos />
                        </Box>
                        <Box className={css.columnLayout}>
                            <SwingFoundations
                                isEditing={isEditing}
                                navigateToEditor={() => handleNavigateToEditor(() => navigate(LINKS.getParameters()))}
                            />
                            <ModuleReorderGroup
                                isEditing={isEditing}
                                ids={kioskModuleIds}
                                onReorder={handleReorder}
                                isLoading={isLoading}
                            >
                                <ModuleRenderer
                                    isEditing={isEditing}
                                    handleRemove={handleRemove}
                                    setAddItemIndex={setAddItemIndex}
                                    modules={kioskModules}
                                />
                            </ModuleReorderGroup>

                            <ModuleAddItem isEditing={isEditing} onAdd={() => setAddItemIndex(kioskModules.length)} />
                        </Box>
                    </Box>
                </Box>
                <Scrubber />
            </ActivityLayout>
            {showSwingThreadForm && (
                <SwingThreadForm activePosition={currentPosition} onClose={toggleSwingThreadForm} />
            )}
            {createLayoutCopy && (
                <NameModal
                    header="Copy layout in order to edit?"
                    label="Layout Name"
                    value={layout?.name + ' (copy)'}
                    onDismiss={toggleCreateLayoutCopy}
                    onSubmit={handleCreateLayoutCopy}
                />
            )}
            {addItemIndex !== null && (
                <ModuleSelectionModal
                    onDismiss={() => setAddItemIndex(null)}
                    onSelect={handleAdd}
                    selectedIds={kioskModuleIds}
                />
            )}
            {isEditingFloor && <FloorViewEditor onDismiss={handleEditingFloorLayout} />}
            {isEditingKeyParam && <KeyParameterEditor onClose={handleEditingKeyParam} />}
        </>
    );
}
